import { Component, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from '@stencil/core';
import { FilterDeactivatedEvent } from '../../03_organisms/lilu-product-list/filter-deactivated-event';
import { ActivatedFilter } from './actived-filter';
import { $$$ } from '../../../utils/utils';

@Component({
  tag: 'lilu-activated-filters',
  styleUrl: 'lilu-activated-filters.scss',
  shadow: true,
})
export class LiluActivatedFilters {
  @Prop() activatedFilters: string;
  @Prop() titleText: string;
  @Prop() resetText: string;
  @Prop() activeTags: string = '';
  @State() internalFilters: ActivatedFilter[] = [];

  componentWillLoad() {
    this.parseFilters();
  }

  componentDidLoad() {
    let el = document.querySelector('lilu-product-filter lilu-modal lilu-link') as HTMLElement;

    if (el !== null) {
      el.addEventListener('click', () => {
        this.onResetAllFilters();
      });
    }
  }

  @Watch('activatedFilters')
  parseFilters() {
    if (this.activatedFilters !== undefined) {
      this.internalFilters = JSON.parse(this.activatedFilters);
    }
  }

  @Event()
  resetAllFilters: EventEmitter<void>;

  /**
   * Trigger an event to tell the parent all filters where removed
   */
  onResetAllFilters() {
    this.internalFilters = [];
    this.resetAllFilters.emit();
  }

  @Event()
  deactivateFilter: EventEmitter<FilterDeactivatedEvent>;

  /**
   * Trigger an event to tell the parent a specific filter got removed
   * @param filterControlName
   * @param value
   */
  onDeactivateFilter(filterControlName: string, value: string) {
    this.internalFilters = this.internalFilters.filter(f => f.value !== value);

    const deactvatedFilter: FilterDeactivatedEvent = {
      filterControlName,
      value,
    };
    this.deactivateFilter.emit(deactvatedFilter);
  }

  /**
   * Adds a filter tag
   * @param filter Filter to be added to the array
   */
  @Method()
  async addFilter(filter: ActivatedFilter) {
    this.internalFilters = [...this.internalFilters, filter];
  }

  /**
   * Updates a filter tag
   * @param filter Filter to be updated in array
   */
  @Method()
  async updateFilter(filter: ActivatedFilter) {
    const internalFilterIndex = this.internalFilters.findIndex(f => f.filterControlName === filter.filterControlName);

    if (internalFilterIndex !== -1) {
      // When filter exists, copy into new array, change and reassign to trigger change detection
      const tempInternalFilters = Object.assign(this.internalFilters);
      tempInternalFilters[internalFilterIndex].value = filter.value;
      tempInternalFilters[internalFilterIndex].label = filter.label;
      this.internalFilters = [...tempInternalFilters];
    }
  }

  /**
   * Remove an filter tag
   * @param filter Filter to be removed from array
   */
  @Method()
  async removeFilter(filter: ActivatedFilter) {
    if (filter.multiSelect) {
      this.internalFilters = this.internalFilters.filter(f => f.value !== filter.value);
    } else {
      this.internalFilters = this.internalFilters.filter(f => f.filterControlName !== filter.filterControlName);
    }
  }

  getRenderInfo() {
    const filterTags = [];

    const internalFiltersInRender = this.internalFilters.filter(f => null !== f && typeof f !== 'undefined');

    if (this.activeTags.length) {
      const activeTagsJSON = JSON.parse(this.activeTags);

      for (let index in activeTagsJSON.tags) {
        const tag = activeTagsJSON.tags[index];
        filterTags.push(<lilu-filter-tag onClick={this.onResetAllFilters.bind(this)}>{tag.type + ': ' + tag.value}</lilu-filter-tag>);
      }
    } else {
      for (let internalFilter of internalFiltersInRender) {
        const parentLabel = $$$(`label[for="lilu_input_${internalFilter.filterControlName}"]`)[0];

        filterTags.push(
          <lilu-filter-tag onClick={this.onDeactivateFilter.bind(this, internalFilter.filterControlName, internalFilter.value)}>
            {parentLabel ? parentLabel.innerText + ': ' + internalFilter.label : internalFilter.label}
          </lilu-filter-tag>,
        );
      }
    }

    const noFilters = this.activeTags.length === 0 && internalFiltersInRender.length === 0;

    return { noFilters: noFilters, filterTags: filterTags };
  }

  render() {
    const renderInfo = this.getRenderInfo();
    return (
      <Host>
        <div class={`activated-filters ${renderInfo.noFilters ? 'no-filters' : ''}`}>
          <div class="heading">
            <lilu-heading color="nero" level="4">
              {this.titleText}
            </lilu-heading>
            <span class="reset-filters" onClick={this.onResetAllFilters.bind(this)}>
              {this.resetText}
            </span>
          </div>
          <div class="filters">{renderInfo.filterTags}</div>
        </div>
      </Host>
    );
  }
}
